import React, { useEffect, useCallback, useState } from 'react'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { useFormik } from 'formik'
import BusinessCustomersActions from "../../../Redux/transition/customers/business-customer.reducer";
import { createCustomHandleApostropheChange, removeTrailingSpacesAndDashes } from '../../../Components/CleanInputs';
import classNames from "classnames";
import { useTranslation, Trans } from "react-i18next";
import CustomToast from '../../../Components/CustomToast'
import { useSelector, useDispatch } from "react-redux";
import "../../../Styles/components/tags.scss";


export const CreateTag = ({
    visible,
    hideTagPopup,
    businessId,
    tagGroupValues,
    setTagGroupValues,
    getSummaryCustomers,
    pageOptions,
    onTagsChanged,
}) => {

    const { t } = useTranslation("common");
    const [errors, setErrors] = useState({});
    const [toastTrigger, setToastTrigger] = useState(false);
    const [clickSave, setClickSave] = useState(false);
    const [showTagErrorMessage, setShowTagErrorMessage] = useState(false);
    const [finalName, setFinalName] = useState("");

    const [tagToast, setTagToast] = useState({
        severity: "",
        summary: "",
        detail: "",
        life: 0
    });

    const dispatch = useDispatch()
    const createTag = useCallback((tagContent) => dispatch(BusinessCustomersActions.createTagRequest(tagContent)), [dispatch]);
    const getTags = useCallback((businessId) => dispatch(BusinessCustomersActions.tagsRequest(businessId)), [dispatch]);
    const getTagGroupsGraphed = useCallback((businessId) => dispatch(BusinessCustomersActions.tagGroupGraphedRequest(businessId)), [dispatch]);

    //Redux State from Store
    const { createdTag, createTagError } = useSelector((state) => state.businessCustomers);
    const creatingSubsTag = useSelector((state) => state.businessCustomers.creatingTag);
    const defMembership = useSelector((state) => state.defMembership.defMembership);

    const createTagErrorMessage = createTagError?.detail?.split('"')[1] ? createTagError?.detail?.split('"')[1] : createTagError?.detail

    useEffect(() => {
        if (!creatingSubsTag && clickSave && createdTag) {
            showTagToast("success", t("tags.success"), t("tags.tag_success"))
        }
        if (!creatingSubsTag && clickSave && !createdTag) {
            showTagToast("error", t("tags.error"), createTagErrorMessage)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [creatingSubsTag, clickSave, createdTag]);

    const showTagToast = (severity, summary, detail) => {
        setTagToast({
            severity,
            summary,
            detail,
            life: 4000,
        });
        setToastTrigger(true);
    }

    const formik = useFormik({
        initialValues: {
            name: ""
        },
        validate: (data) => {
            let errors = {};
            if (!data.name) {
                errors.name = t("tags.tag_name_required");
            }
            return errors;
        },
        onSubmit: (data) => {
            setClickSave(true);

            if (!tagGroupValues?.id) {
                return;
            }

            const tagContent = {
                name: removeTrailingSpacesAndDashes(data.name) || null,
                business: { id: businessId },
                group: { id: tagGroupValues.id }
            };
            setFinalName(tagContent.name);
            createTag(tagContent);
        },
    }
    );

    const commonParams = [formik, setErrors, t];
    const customHandleChange = createCustomHandleApostropheChange(...commonParams);

    const isFormFieldValid = (name) =>
        !!(formik.touched[name] && formik.errors[name]);

    const getFormErrorMessage = (name) => {
        return (
            isFormFieldValid(name) && (
                <small className="character_errors">{formik.errors[name]}</small>
            )
        );
    };

    const tagGroupName = tagGroupValues?.name ?? "";

    return (
        <div>
            <Dialog
                visible={visible}
                onHide={() => {
                    setTagGroupValues(null);
                    formik.setFieldValue('name', '');
                    hideTagPopup();
                }}
                header={t("tags.create_tag_label")}
                style={{ width: "350px" }}
            >
                <CustomToast
                    {...tagToast}
                    trigger={toastTrigger}
                    handleActionsAfterToast={() => {
                        setToastTrigger(false);
                        setClickSave(false);
                        hideTagPopup();
                        if (getSummaryCustomers) {
                            getSummaryCustomers(defMembership.business.id, pageOptions);
                        }
                        getTagGroupsGraphed(defMembership.business.id);
                        getTags(defMembership.business.id);
                        onTagsChanged?.(tagGroupValues?.id);
                        formik.resetForm();
                        setShowTagErrorMessage(false);
                        setFinalName("")
                        setTagGroupValues(null);
                        formik.setFieldValue('name', '');
                    }}
                    handleActionsAfterErrorToast={() => {
                        setShowTagErrorMessage(true);
                        setToastTrigger(false);
                        setClickSave(false);
                    }}
                />

                <form onSubmit={formik.handleSubmit} className="p-fluid p-3" style={{ paddingInline: "4px" }}>
                    <div className="p-field p-mb-3" >
                        {tagGroupName ? (
                            <div className="tags-dialog-tag-group-context mb-3">
                                <span className="createdOnLabel">
                                    {t("tags.tag_group_label")}
                                    {":"}
                                </span>
                                <span className="name-body">{tagGroupName}</span>
                            </div>
                        ) : null}

                        <div className='mt-4'>
                            <label
                                id='name_label'
                                htmlFor="name"
                                className={classNames("product-form-label", {
                                    "": isFormFieldValid("name")
                                })}  >{t("tags.tag_label")}   </label>
                            <InputText
                                id="name"
                                name="name"
                                value={formik.values.name}
                                onChange={(e) => {
                                    customHandleChange(e);
                                    setShowTagErrorMessage(false);
                                }}
                                style={{ width: "100%" }}
                                maxLength={25}
                            />
                            {getFormErrorMessage("name")}
                            {errors.name ? <p className="character_errors">{errors.name}</p> : <p className='tag-field-hint'>{t("tags.tag_field_hint")}</p>}

                            {showTagErrorMessage ?
                                <div className='mt-1'>
                                    <Trans
                                        i18nKey="tags.message_tag_name_exists"
                                        values={{
                                            tagName: finalName
                                        }}
                                        components={{
                                            1: <span className="tags_error_message" />,
                                            2: <span className="tags_error_message_bold" />,
                                            3: <span className="tags_error_message" />
                                        }}
                                    />
                                </div>
                                : null}
                        </div>
                    </div>

                    <div className="p-field p-mb-3 mt-4" >
                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "2rem" }}>
                            <Button
                                type="button"
                                label={t('subscriber.cancel')}
                                className="mr-1"
                                icon="pi pi-times"
                                onClick={() => {
                                    hideTagPopup();
                                }}
                                id='cancel-tag-button'
                            />
                            <Button
                                type="submit"
                                label={t("products.save_product")}
                                className="p-mt-2 save-btns"
                                disabled={creatingSubsTag || !formik.values.name || clickSave || !tagGroupValues}
                                loading={creatingSubsTag} />
                        </div>
                    </div>
                </form>
            </Dialog>
        </div >
    )
}